import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useSpotlight } from "../spotlight/SpotlightContext"
import { useTerminal } from "../terminal/TerminalContext"
import { DESKTOP_APPS } from "./desktopApps"
import { isProductivityHubEnabled } from "./desktopStorage"
import { useDesktopWorkspace } from "./useDesktopWorkspace"
import {
  HOLOS_ACTION_EVENT,
  newProductivityId,
} from "./productivityStorage"

const HolosProductivityCtx = createContext(null)
const FOCUS_KEY = "holos_focus_mode"
const WINDOW_PREFS = {
  "live-chat": "chatWindowEnabled",
  "universal-inbox": "inboxEnabled",
  "agent-control": "agentControlEnabled",
  "mission-control": "missionControlEnabled",
  "holos-workspaces": "splitWorkspacesEnabled",
  "holos-rules": "ruleBookEnabled",
  "ai-control": "aiControlEnabled",
  "file-manager": "holosFilesEnabled",
  "global-search": "globalSearchEnabled",
  "shared-workspace": "sharedWorkspaceEnabled",
  "resource-governor": "resourceGovernorEnabled",
  "permission-center": "permissionCenterEnabled",
  "app-studio": "appStudioEnabled",
}
const VALID_WINDOW_IDS = new Set(Object.values(DESKTOP_APPS).map((app) => app.windowId).filter(Boolean))
const windowEnabled = (typeId, prefs) => VALID_WINDOW_IDS.has(typeId)
  && (typeId !== "productivity" || isProductivityHubEnabled(prefs))
  && (!WINDOW_PREFS[typeId] || prefs[WINDOW_PREFS[typeId]] !== false)

const todayKey = (date = new Date()) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, "0"),
  String(date.getDate()).padStart(2, "0"),
].join("-")
const nowTime = (date = new Date()) => date.toTimeString().slice(0, 5)

export function HolosProductivityProvider({ children }) {
  const spotlight = useSpotlight()
  const { openTerminal } = useTerminal()
  const { productivity: data, setProductivity: setData, prefs, accountId } = useDesktopWorkspace()
  const focusKey = `${FOCUS_KEY}:${accountId || "signed-out"}`
  const [focusActive, setFocusActiveState] = useState(() => sessionStorage.getItem(focusKey) === "1")
  const [recording, setRecording] = useState(false)
  const recordedActions = useRef([])

  useEffect(() => {
    setFocusActiveState(sessionStorage.getItem(focusKey) === "1")
  }, [focusKey])

  const setFocusActive = useCallback((value) => {
    const next = typeof value === "function" ? value(sessionStorage.getItem(focusKey) === "1") : value
    setFocusActiveState(!!next)
    sessionStorage.setItem(focusKey, next ? "1" : "0")
  }, [focusKey])

  const addNote = useCallback((text) => {
    const value = String(text ?? "").trim().slice(0, 4000)
    if (!value) return
    setData((prev) => ({ ...prev, notes: [{ id: newProductivityId("note"), text: value, pinned: false, createdAt: Date.now() }, ...prev.notes].slice(0, 80) }))
  }, [setData])

  const removeNote = useCallback((id) => setData((prev) => ({ ...prev, notes: prev.notes.filter((item) => item.id !== id) })), [setData])

  const addShelfItem = useCallback((item) => {
    const value = String(item?.value ?? "").trim().slice(0, 8000)
    if (!value) return
    const type = ["url", "file"].includes(item?.type) ? item.type : "text"
    setData((prev) => ({
      ...prev,
      shelf: [{
        id: newProductivityId("shelf"), type, value,
        label: String(item?.label || value).slice(0, 120),
        preview: String(item?.preview ?? "").slice(0, 3000),
        createdAt: Date.now(),
      }, ...prev.shelf].slice(0, 60),
    }))
  }, [setData])

  const removeShelfItem = useCallback((id) => setData((prev) => ({ ...prev, shelf: prev.shelf.filter((item) => item.id !== id) })), [setData])

  const addClipboard = useCallback((text) => {
    const value = String(text ?? "").trim().slice(0, 4000)
    if (!value) return
    setData((prev) => {
      if (prev.clipboard[0]?.text === value) return prev
      return { ...prev, clipboard: [{ id: newProductivityId("clip"), text: value, createdAt: Date.now() }, ...prev.clipboard].slice(0, 30) }
    })
  }, [setData])
  const clearClipboard = useCallback(() => setData((prev) => ({ ...prev, clipboard: [] })), [setData])

  useEffect(() => {
    if (!prefs.clipboardHistoryEnabled) return undefined
    const capture = () => {
      const selected = window.getSelection()?.toString()
      if (selected) addClipboard(selected)
    }
    document.addEventListener("copy", capture)
    return () => document.removeEventListener("copy", capture)
  }, [addClipboard, prefs.clipboardHistoryEnabled])

  const captureSession = useCallback((name, desktopId = "") => {
    const session = {
      id: newProductivityId("session"),
      name: String(name || "Holos Sitzung").trim().slice(0, 80),
      desktopId,
      windows: spotlight.windows.map(({ typeId, x, y, w, h, minimized }) => ({ typeId, x, y, w, h, minimized })),
      schedule: { enabled: false, time: "09:00", days: [], lastRun: "" },
      createdAt: Date.now(),
    }
    setData((prev) => ({ ...prev, sessions: [session, ...prev.sessions].slice(0, 24) }))
    return session
  }, [setData, spotlight.windows])

  const activateSession = useCallback((session) => {
    if (!session) return
    spotlight.restoreWindows(session.windows.filter((item) => windowEnabled(item.typeId, prefs)))
    if (session.desktopId) window.dispatchEvent(new CustomEvent("holos-switch-desktop", { detail: session.desktopId }))
  }, [prefs, spotlight])

  const updateSession = useCallback((id, patch) => setData((prev) => ({
    ...prev,
    sessions: prev.sessions.map((item) => item.id === id ? { ...item, ...patch } : item),
  })), [setData])
  const removeSession = useCallback((id) => setData((prev) => ({ ...prev, sessions: prev.sessions.filter((item) => item.id !== id) })), [setData])

  const runRoutine = useCallback((routine) => {
    const session = data.sessions.find((item) => item.id === routine.sessionId)
    if (session) activateSession(session)
    routine.apps.forEach((appId) => {
      const windowId = DESKTOP_APPS[appId]?.windowId
      if (windowId && windowEnabled(windowId, prefs)) spotlight.openWindow(windowId, openTerminal)
    })
    if (routine.focus && prefs.focusModeEnabled) setFocusActive(true)
  }, [activateSession, data.sessions, openTerminal, prefs, setFocusActive, spotlight])

  const upsertRoutine = useCallback((routine) => setData((prev) => {
    const value = { ...routine, id: routine.id || newProductivityId("routine") }
    const exists = prev.routines.some((item) => item.id === value.id)
    return { ...prev, routines: exists ? prev.routines.map((item) => item.id === value.id ? value : item) : [value, ...prev.routines].slice(0, 24) }
  }), [setData])
  const removeRoutine = useCallback((id) => setData((prev) => ({ ...prev, routines: prev.routines.filter((item) => item.id !== id) })), [setData])

  const playMacro = useCallback((macro) => {
    macro?.actions?.forEach((action, index) => {
      window.setTimeout(() => {
        if (action.type === "open") {
          const typeId = DESKTOP_APPS[action.value]?.windowId || action.value
          if (windowEnabled(typeId, prefs)) spotlight.openWindow(typeId, openTerminal)
        }
        if (action.type === "layout") spotlight.arrangeWindows(action.value)
        if (action.type === "desktop") window.dispatchEvent(new CustomEvent("holos-switch-desktop", { detail: action.value }))
        if (action.type === "focus" && prefs.focusModeEnabled) setFocusActive(action.value === "on")
      }, index * 160)
    })
  }, [openTerminal, prefs, setFocusActive, spotlight])

  const startMacroRecording = useCallback(() => {
    recordedActions.current = []
    setRecording(true)
  }, [])
  const stopMacroRecording = useCallback((name) => {
    setRecording(false)
    if (!recordedActions.current.length) return
    const macro = { id: newProductivityId("macro"), name: String(name || "Holos Makro").slice(0, 80), actions: recordedActions.current.slice(0, 30) }
    setData((prev) => ({ ...prev, macros: [macro, ...prev.macros].slice(0, 24) }))
    recordedActions.current = []
  }, [setData])
  const removeMacro = useCallback((id) => setData((prev) => ({ ...prev, macros: prev.macros.filter((item) => item.id !== id) })), [setData])

  useEffect(() => {
    if (!recording) return undefined
    const capture = (event) => {
      const action = event.detail
      if (action && ["open", "layout", "desktop", "focus"].includes(action.type)) recordedActions.current.push(action)
    }
    window.addEventListener(HOLOS_ACTION_EVENT, capture)
    return () => window.removeEventListener(HOLOS_ACTION_EVENT, capture)
  }, [recording])

  useEffect(() => {
    if (!prefs.sessionsEnabled && !prefs.routinesEnabled) return undefined
    const checkSchedules = () => {
      const now = new Date()
      const dateKey = todayKey(now)
      const time = nowTime(now)
      const dueSessions = data.sessions.filter((session) => prefs.sessionsEnabled && session.schedule.enabled && session.schedule.time === time && session.schedule.days.includes(now.getDay()) && session.schedule.lastRun !== dateKey)
      const dueRoutines = data.routines.filter((routine) => prefs.routinesEnabled && routine.enabled && routine.time === time && routine.days.includes(now.getDay()) && routine.lastRun !== dateKey)
      dueSessions.forEach(activateSession)
      dueRoutines.forEach(runRoutine)
      if (dueSessions.length || dueRoutines.length) setData((prev) => ({
        ...prev,
        sessions: prev.sessions.map((session) => dueSessions.some((item) => item.id === session.id) ? { ...session, schedule: { ...session.schedule, lastRun: dateKey } } : session),
        routines: prev.routines.map((routine) => dueRoutines.some((item) => item.id === routine.id) ? { ...routine, lastRun: dateKey } : routine),
      }))
    }
    checkSchedules()
    const timer = setInterval(checkSchedules, 30000)
    return () => clearInterval(timer)
  }, [activateSession, data.routines, data.sessions, prefs.routinesEnabled, prefs.sessionsEnabled, runRoutine, setData])

  const value = useMemo(() => ({
    data, prefs, focusActive, recording,
    setFocusActive, addNote, removeNote, addShelfItem, removeShelfItem, addClipboard, clearClipboard,
    captureSession, activateSession, updateSession, removeSession,
    runRoutine, upsertRoutine, removeRoutine,
    playMacro, startMacroRecording, stopMacroRecording, removeMacro,
  }), [data, prefs, focusActive, recording, setFocusActive, addNote, removeNote, addShelfItem, removeShelfItem, addClipboard, clearClipboard, captureSession, activateSession, updateSession, removeSession, runRoutine, upsertRoutine, removeRoutine, playMacro, startMacroRecording, stopMacroRecording, removeMacro])

  return <HolosProductivityCtx.Provider value={value}>{children}</HolosProductivityCtx.Provider>
}

export function useHolosProductivity() {
  const context = useContext(HolosProductivityCtx)
  if (!context) throw new Error("useHolosProductivity requires HolosProductivityProvider")
  return context
}

export default HolosProductivityProvider
